import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Image, FileText, BookMarked, Heart, Video, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";

// Hero Slider Data
const heroSlides = [
    {
        id: 1,
        category: 'Flayer Dakwah',
        title: 'Dakwah Visual yang Menginspirasi',
        desc: 'Flyer dan infografis islami dari Thubaproject untuk disebarluaskan.',
        icon: Image,
        path: '/belajar-islam/infografis',
        gradient: 'from-emerald-900/80 via-[#081c1a]/90 to-[#081c1a]',
        accent: '#10b981',
    },
    {
        id: 2,
        category: 'Artikel',
        title: 'Ilmu yang Bermanfaat untuk Umat',
        desc: 'Tulisan dan artikel kajian Islam yang disusun berdasarkan rujukan ilmiah dan sumber terpercaya.',
        icon: FileText,
        path: '/belajar-islam/artikel',
        gradient: 'from-blue-900/80 via-[#081c1a]/90 to-[#081c1a]',
        accent: '#3b82f6',
    },
    {
        id: 3,
        category: 'Panduan Ibadah',
        title: 'Panduan Lengkap Ibadah',
        desc: 'Tata cara shalat, panduan haji & umroh, dan amalan lainnya.',
        icon: BookMarked,
        path: '/belajar-islam/panduan-ibadah',
        gradient: 'from-amber-900/80 via-[#081c1a]/90 to-[#081c1a]',
        accent: '#f59e0b',
    },
    {
        id: 4,
        category: 'Doa & Dzikir',
        title: 'Hiasi Hari dengan Dzikir & Doa',
        desc: 'Doa harian pendek dan dzikir pagi petang untuk meraih ketenangan jiwa.',
        icon: Heart,
        path: '/belajar-islam/doa-dzikir',
        gradient: 'from-rose-900/80 via-[#081c1a]/90 to-[#081c1a]',
        accent: '#f43f5e',
    },
    {
        id: 5,
        category: 'Kajian',
        title: 'Kajian Ilmiah',
        desc: 'Kumpulan video kajian dari channel YouTube resmi Thubaproject.',
        icon: Video,
        path: '/belajar-islam/kajian',
        gradient: 'from-purple-900/80 via-[#081c1a]/90 to-[#081c1a]',
        accent: '#a855f7',
    },
];

const BelajarIslam = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef(null);

    // Auto-play slider
    useEffect(() => {
        if (!isPaused) {
            intervalRef.current = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
            }, 4500);
        }
        return () => clearInterval(intervalRef.current);
    }, [isPaused]);

    const goToSlide = (idx) => {
        setCurrentSlide(idx);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 8000);
    };

    const prevSlide = () => goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
    const nextSlide = () => goToSlide((currentSlide + 1) % heroSlides.length);

    const slide = heroSlides[currentSlide];
    const SlideIcon = slide.icon;

    return (
        <div className="pt-20 min-h-screen bg-[#081c1a] animate-fade-in-up">

            {/* ===== HERO SLIDER ===== */}
            <section
                className="relative h-[85vh] min-h-[500px] overflow-hidden flex items-center"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
                {/* Background slides */}
                {heroSlides.map((s, idx) => (
                    <div
                        key={s.id}
                        className={`absolute inset-0 transition-opacity duration-1000 ease-in-out bg-gradient-to-br ${s.gradient}
              ${idx === currentSlide ? 'opacity-100' : 'opacity-0'}`}
                    >
                        <div className="absolute inset-0"
                            style={{
                                background: `radial-gradient(ellipse at 30% 50%, ${s.accent}22 0%, transparent 60%)`,
                            }}
                        />
                    </div>
                ))}

                {/* Decorative pattern */}
                <div className="absolute inset-0 opacity-5"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d4af37\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}
                />

                {/* Slide Content */}
                <div className="relative z-10 container mx-auto px-20 md:px-24">
                    <div className="max-w-3xl">
                        {heroSlides.map((s, idx) => {
                            return (
                                <div
                                    key={s.id}
                                    className={`transition-all duration-700 ${idx === currentSlide
                                        ? 'opacity-100 translate-y-0'
                                        : 'opacity-0 translate-y-8 absolute pointer-events-none'
                                        }`}
                                >

                                    <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6 leading-tight">
                                        {s.title}
                                    </h1>
                                    <p className="text-xl text-white/70 mb-10 max-w-2xl leading-relaxed">
                                        {s.desc}
                                    </p>
                                    <div className="flex gap-4 flex-wrap">
                                        <Link to={s.path}>
                                            <Button
                                                className="font-bold px-8 py-6 rounded-full text-black shadow-lg transition-all duration-300 hover:scale-105"
                                                style={{ backgroundColor: s.accent, boxShadow: `0 0 20px ${s.accent}44` }}
                                            >
                                                Lihat Konten <ArrowRight className="ml-2 text-[#d4af37]" size={18} />
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Navigation Arrows */}
                <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 border border-white/10"
                >
                    <ChevronLeft size={22} className="text-[#d4af37]" />
                </button>
                <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 border border-white/10"
                >
                    <ChevronRight size={22} className="text-[#d4af37]" />
                </button>

                {/* Dot Indicators */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
                    {heroSlides.map((s, idx) => (
                        <button
                            key={s.id}
                            onClick={() => goToSlide(idx)}
                            className="transition-all duration-300 rounded-full"
                            style={{
                                width: idx === currentSlide ? '32px' : '8px',
                                height: '8px',
                                backgroundColor: idx === currentSlide ? s.accent : 'rgba(255,255,255,0.3)',
                            }}
                        />
                    ))}
                </div>
            </section >

            {/* ===== KONTEN TERBARU ===== */}
            < section id="konten" className="py-24 bg-[#081c1a]" >
                <div className="container mx-auto px-6">
                    <div className="text-center mb-10">
                        <h2 className="text-4xl font-serif font-bold text-white">Ilmu <span className="text-[#d4af37]">Syar'i</span></h2>
                        <p className="text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
                            Beragam konten yang menghadirkan ilmu dan inspirasi untuk meningkatkan kualitas iman dan amal berdasarkan Al-Qur'an dan Sunnah sesuai dengan pemahaman para Salafush Shalih.
                        </p>
                    </div>

                    {/* Cards Kategori */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 max-w-6xl mx-auto">
                        {heroSlides.map((s) => {
                            const Icon = s.icon;
                            return (
                                <Link
                                    key={s.id}
                                    to={s.path}
                                    className="block group"
                                >
                                    <div className="bg-[#0D2B29] border border-white/10 group-hover:border-[#d4af37]/50 rounded-2xl p-8 flex flex-col gap-6 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.2)] h-full justify-between">

                                        <div className="flex flex-col gap-4">
                                            <div className="w-14 h-14 rounded-full bg-[#d4af37]/10 flex items-center justify-center group-hover:bg-[#d4af37]/20 transition-colors duration-300">
                                                <Icon size={28} className="text-[#d4af37]" />
                                            </div>

                                            <div>
                                                <span className="text-xs font-bold tracking-widest uppercase text-emerald-400 block mb-2">{s.category}</span>
                                                <h3 className="text-white font-serif font-semibold text-xl group-hover:text-[#d4af37] transition-colors leading-snug">{s.title}</h3>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 text-white/70 text-sm font-medium group-hover:text-[#d4af37] transition-colors mt-4">
                                            Baca selengkapnya <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform text-[#d4af37]" />
                                        </div>

                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section >
        </div >
    );
};

export default BelajarIslam;
